import { StyledButton } from "@aureskonnect/react-ui";
import { ELECTRON_MODE, WEBVIEW_MODE } from "@constants";
import { t } from "i18next";

import React from "react";
import { Row } from "react-bootstrap";
import { FaSync, FaTimes } from "react-icons/fa";

import { FlexboxGrid } from "@components/common/FlexboxGrid";
import SoftaveraLogo from "@components/SvgIcons/SoftaveraLogo";

import { setIsAuthenticationModalOpened } from "@pages/Kiosk/store";

type PropsType = {
    toggleSyncModal: () => void;
};

export function AppNotSynchronizedModalWrapper({
    toggleSyncModal,
}: PropsType): JSX.Element {
    function handleCloseApplication() {
        const closeApp = "close_app";
        const url = `http://localhost:6009/api/v1/kiosk?systemAction=${closeApp}`;

        if (process.env.REACT_APP_MODE === WEBVIEW_MODE) {
            fetch(url).catch((error) =>
                console.log(
                    `Error while using system action with action: "${closeApp}" and Error: "${error}"`
                )
            );
        } else if (process.env.REACT_APP_MODE === ELECTRON_MODE) {
            (window as RendererWindow).electronAPI?.sendQuitEvent();
        }
    }

    return (
        <div
            style={{
                minHeight: "100vh",
                minWidth: "100vw",
                background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                display: "grid",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "2rem",
                gridTemplateRows: "20vh 50vh",
            }}
        >
            <Row>
                <div style={{ marginBottom: "2rem" }}>
                    <SoftaveraLogo />
                </div>
            </Row>
            <div style={{ width: "100%", maxWidth: "500px" }}>
                {/* Header */}
                <div style={{ textAlign: "center", marginBottom: "3rem" }}>
                    <h1
                        style={{
                            fontSize: "var(--font-size-subtitle)",
                            lineHeight: "38px",
                            letterSpacing: "0px",
                            color: "#1f2937",
                            opacity: 1,
                            whiteSpace: "nowrap",
                            marginBottom: "0.5rem",
                            fontWeight: "600",
                        }}
                    >
                        {t("App not synchronized")}
                    </h1>
                    <p
                        style={{
                            color: "#65b6d5",
                            fontSize: "var(--font-size-base)",
                            margin: 0,
                        }}
                    >
                        {t(
                            "Your application needs to be synchronized to continue"
                        )}
                    </p>
                </div>

                {/* Main Card */}
                <div
                    style={{
                        backgroundColor: "#ffffff",
                        borderRadius: "20px",
                        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                        border: "1px solid #e5e7eb",
                        padding: "3rem",
                    }}
                >
                    <FlexboxGrid
                        justifyContentCentered={true}
                        alignItemsCentered={true}
                        gap="30px"
                        className="flex-column"
                    >
                        {/* Sync Animation */}
                        <div
                            style={{
                                textAlign: "center",
                                marginBottom: "1rem",
                            }}
                        >
                            <div
                                style={{
                                    height: "150px",
                                    width: "150px",
                                    margin: "0 auto",
                                    borderRadius: "50%",
                                    background:
                                        "linear-gradient(135deg, #077dbc 0%, #075d9b 100%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    position: "relative",
                                    boxShadow:
                                        "0 10px 25px -5px rgba(7, 125, 188, 0.3)",
                                }}
                            >
                                {/* Outer rotating ring */}
                                <div
                                    style={{
                                        position: "absolute",
                                        top: "-10px",
                                        left: "-10px",
                                        right: "-10px",
                                        bottom: "-10px",
                                        border: "3px solid transparent",
                                        borderTop: "3px solid #65b6d5",
                                        borderRight: "3px solid #65b6d5",
                                        borderRadius: "50%",
                                        animation:
                                            "syncRotate 2s linear infinite",
                                    }}
                                />

                                {/* Inner sync arrows */}
                                <div
                                    style={{
                                        position: "relative",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <FaSync
                                        style={{
                                            fontSize: "var(--font-size-title)",
                                            color: "#ffffff",
                                            animation:
                                                "syncPulse 2s ease-in-out infinite",
                                        }}
                                    />
                                </div>

                                {/* Floating dots */}
                                <div
                                    style={{
                                        position: "absolute",
                                        top: "20px",
                                        right: "20px",
                                        width: "8px",
                                        height: "8px",
                                        backgroundColor: "#65b6d5",
                                        borderRadius: "50%",
                                        animation:
                                            "syncFloat 3s ease-in-out infinite",
                                    }}
                                />
                                <div
                                    style={{
                                        position: "absolute",
                                        bottom: "25px",
                                        left: "15px",
                                        width: "6px",
                                        height: "6px",
                                        backgroundColor: "#ffffff",
                                        borderRadius: "50%",
                                        animation:
                                            "syncFloat 3s ease-in-out infinite 1s",
                                    }}
                                />
                                <div
                                    style={{
                                        position: "absolute",
                                        top: "35px",
                                        left: "35px",
                                        width: "4px",
                                        height: "4px",
                                        backgroundColor: "#65b6d5",
                                        borderRadius: "50%",
                                        animation:
                                            "syncFloat 3s ease-in-out infinite 2s",
                                    }}
                                />
                            </div>
                        </div>

                        {/* Message */}
                        <div
                            style={{
                                textAlign: "center",
                                marginBottom: "2rem",
                            }}
                        >
                            <p
                                style={{
                                    fontSize: "var(--font-size-base)",
                                    color: "#6b7280",
                                    lineHeight: "1.6",
                                    margin: 0,
                                }}
                            >
                                {t(
                                    "Please synchronize your application to access all features and ensure proper functionality"
                                )}
                            </p>
                        </div>

                        {/* Buttons */}
                        <div
                            style={{
                                width: "100%",
                                display: "flex",
                                flexDirection: "column",
                                gap: "16px",
                            }}
                        >
                            <StyledButton
                                style={{
                                    width: "100%",
                                    height: "60px",
                                    fontSize: "var(--font-size-base)",
                                    color: "var(--font-color-base)",
                                    fontWeight: "600",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "12px",
                                    borderRadius: "12px",
                                    border: "none",
                                    boxShadow:
                                        "0 10px 25px -5px rgba(85, 115, 153, 0.4)",
                                    transition: "all 0.2s ease",
                                    background:
                                        "linear-gradient(135deg, #077dbc 0%, #075d9b 100%)",
                                }}
                                variant="primary"
                                rounded={true}
                                block={true}
                                onClick={() => {
                                    toggleSyncModal();
                                    setIsAuthenticationModalOpened(true);
                                }}
                            >
                                <FaSync
                                    style={{
                                        fontSize: "var(--font-size-base)",
                                    }}
                                />
                                {t("Sync My App")}
                            </StyledButton>

                            <StyledButton
                                style={{
                                    width: "100%",
                                    height: "60px",
                                    fontSize: "var(--font-size-base)",
                                    fontWeight: "500",
                                    borderRadius: "12px",
                                    backgroundColor: "#ffffff",
                                    border: "2px solid #e5e7eb",
                                    color: "#374151",
                                    transition: "all 0.2s ease",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "12px",
                                }}
                                outline={true}
                                rounded={true}
                                variant="light"
                                block={true}
                                onClick={handleCloseApplication}
                            >
                                <FaTimes
                                    style={{
                                        fontSize: "var(--font-size-base)",
                                    }}
                                />
                                {t("Leave")}
                            </StyledButton>
                        </div>
                    </FlexboxGrid>
                </div>

                {/* Footer */}
                <div style={{ textAlign: "center", marginTop: "2rem" }}>
                    <p
                        style={{
                            fontSize: "var(--font-size-small)",
                            color: "#9ca3af",
                            margin: 0,
                        }}
                    >
                        {t(
                            "Synchronization ensures your app has the latest updates and configurations"
                        )}
                    </p>
                </div>
            </div>

            {/* CSS Animations */}
            <style>{`
                @keyframes syncRotate {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                @keyframes syncPulse {
                    0%, 100% { 
                        transform: scale(1) rotate(0deg);
                        opacity: 1;
                    }
                    50% { 
                        transform: scale(1.1) rotate(180deg);
                        opacity: 0.8;
                    }
                }
                
                @keyframes syncFloat {
                    0%, 100% { 
                        transform: translateY(0px);
                        opacity: 0.6;
                    }
                    50% { 
                        transform: translateY(-10px);
                        opacity: 1;
                    }
                }
            `}</style>
        </div>
    );
}
